// Grace period after a failed renewal (Mike 04.10.: 7 days). GET /api/auth/me carries paymentIssue and
// graceUntil (ISO, from 3a-2). Pure logic, unit-tested from server/__tests__.

export type GraceFields = { paymentIssue?: boolean | null; graceUntil?: string | null };

/** "11.10." in Berlin time; null for a missing or broken date. */
export function formatGraceDate(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return new Intl.DateTimeFormat("de-DE", { day: "2-digit", month: "2-digit", timeZone: "Europe/Berlin" }).format(d);
}

/** Banner text while a payment failed; null = no banner. After the grace period the 402 / PaymentStep takes over. */
export function graceBannerText(user: GraceFields | null | undefined): string | null {
  if (!user?.paymentIssue) return null;
  const until = formatGraceDate(user.graceUntil);
  return until
    ? `Ihre letzte Zahlung ist fehlgeschlagen. Bitte aktualisieren Sie Ihre Zahlungsmethode bis ${until}, sonst pausieren wir Ihren Zugang.`
    : "Ihre letzte Zahlung ist fehlgeschlagen. Bitte aktualisieren Sie Ihre Zahlungsmethode, sonst pausieren wir Ihren Zugang.";
}

const ACCESS_STATE_LABEL: Record<string, string> = {
  trial: "Testphase aktiv",
  active: "Abonnement aktiv",
  pending_payment: "Kein aktives Abonnement",
  expired: "Abonnement abgelaufen",
  disabled: "Zugang deaktiviert",
};

export type SubscriptionFields = GraceFields & {
  accessState?: string | null;
  subscriptionExpiresAt?: string | Date | null;
};

/**
 * Status lines of the "Abonnement & Abrechnung" card on /profil. During the grace period it repeats the banner
 * instead of "Abonnement aktiv · Nächste Abrechnung am <grace end>" (subscription_expires_at is then the grace
 * end, not a billing date).
 */
export function subscriptionSummary(account: SubscriptionFields | null | undefined): {
  label: string;
  detail: string | null;
  paymentFailed: boolean;
  /** "verlängert sich automatisch" note; hidden during the grace, where it contradicts the status. */
  renewalNote: boolean;
} {
  const banner = graceBannerText(account);
  if (banner) return { label: "Zahlung fehlgeschlagen", detail: banner, paymentFailed: true, renewalNote: false };
  const state = account?.accessState ?? "";
  const label = ACCESS_STATE_LABEL[state] ?? "Status unbekannt";
  const raw = account?.subscriptionExpiresAt;
  const d = raw ? new Date(raw) : null;
  if (!d || Number.isNaN(d.getTime()) || (state !== "trial" && state !== "active")) {
    return { label, detail: null, paymentFailed: false, renewalNote: true };
  }
  const date = new Intl.DateTimeFormat("de-DE", { timeZone: "Europe/Berlin" }).format(d);
  return { label, detail: `${state === "trial" ? "Testphase bis " : "Nächste Abrechnung am "}${date}`, paymentFailed: false, renewalNote: true };
}
